import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '../common/Card/Card'
import type { MetricOption } from '../../types/Stats'
import { Dropdown } from 'primereact/dropdown'
import { Message } from 'primereact/message'
import './StatsTopBarSelector.css'
import { useLocalizationLabels } from '../../context/LocalizationLabelContext';
import { useStatsTopBarOptions } from '../../hooks/useStatsTopBarOptions'

interface Props {
  regions:        string[]
  scope:          string
  onScopeChange:  (scope: string) => void
  geo:            string
  onGeoChange:    (geo: string) => void
  metric:         string | null
  onMetricChange: (metric: string | null) => void
  sources:        string[]
  currentSource:  string
  onSourceChange: (src: string) => void
}

function StatsTopBarSelector({
  regions, scope, onScopeChange,
  geo, onGeoChange,
  metric, onMetricChange,
  sources, currentSource, onSourceChange,
}: Props) {
  const { t} = useTranslation('mainStats')
  const { geoNameOptionLabels, statsLabels } = useLocalizationLabels()
  const { rawGeoOptions, rawMetricOptions, geoError, metricError } =
    useStatsTopBarOptions(scope, geo)
  const [scopeIcons, setScopeIcons] = useState<Record<string, string>>({})

  // Ikony do Scope kartičiek
  useEffect(() => {
    const controller = new AbortController()
    fetch('/config/scopes.json', { signal: controller.signal })
      .then(r => r.json())
      .then((cfg: Record<string, { icon: string }>) => {
        const map: Record<string, string> = {}
        for (const [k, v] of Object.entries(cfg)) map[k] = v.icon
        setScopeIcons(map)
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        console.error('Failed to load scopes.json (FE config — verify deploy)', err)
      })
    return () => controller.abort()
  }, [])
  
  const geoOptions = useMemo(
      () => rawGeoOptions.map(g => ({ ...g, name: geoNameOptionLabels[g.id]?.label ?? g.id })),
      [rawGeoOptions, geoNameOptionLabels],
  )

  const metricOptions: MetricOption[] = useMemo(
    () => rawMetricOptions.map(m => ({
      id:          m.id,
      name:        statsLabels.metrics?.[m.id]?.name        ?? m.id,
      description: statsLabels.metrics?.[m.id]?.description ?? '',
    })),
    [rawMetricOptions, statsLabels],
  )

  const displayMetric = useMemo(() => {
    if (!metric) return null
    const fromOptions = metricOptions.find(m => m.id === metric)
    if (fromOptions) return fromOptions
    const labelDoc = statsLabels.metrics?.[metric]
    return {
      id:          metric,
      name:        labelDoc?.name        ?? metric,
      description: labelDoc?.description ?? '',
    }
  }, [metric, metricOptions, statsLabels])

  return (
    <div className="top-bar-selector">
      {/* Scope karty */}
      <div className="analyses-nav">
        {regions.map((r) => (
          <Card
            key={r}
            className={`scope-card ${r === scope ? 'active' : ''}`}
            onClick={() => onScopeChange(r)}
          >
            <span className="material-symbols-rounded">
              {scopeIcons[r] ?? scopeIcons['_default'] ?? 'bar_chart'}
            </span>
            <h4>{statsLabels.scope?.[r] ?? r}</h4>
          </Card>
        ))}
      </div>
      {/* Dropdowny */}
      <div className="main-selectors">
        <div className="selector-wrapper">
          <span className="p-float-label">
            <Dropdown
              inputId="geo-select"
              value={geo || null}
              options={geoOptions}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => onGeoChange(e.value ?? '')}
              showClear
              disabled={geoError}
            />
            <label htmlFor="geo-select">{t('selector.selectTerritory')}</label>
          </span>
          {geoError && (
            <Message severity="error" text={t('selector.loadError')} className="selector-error" />
          )}
        </div>
        <div className="selector-wrapper">
          <span className="p-float-label">
            <Dropdown
              inputId="metric-select"
              value={metric}
              options={metricOptions}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => onMetricChange(e.value ?? null)}
              disabled={!geo || metricError}
              showClear
            />
            <label htmlFor="metric-select">{t('selector.selectMetric')}</label>
          </span>
          {metricError && (
            <Message severity="error" text={t('selector.loadError')} className="selector-error" />
          )}
        </div>
        {sources.length > 0 && (
          <div className="selector-wrapper">
            <span className="p-float-label">
              {sources.length > 1 ? (
                <Dropdown
                  inputId="source-select"
                  value={currentSource}
                  options={sources.map(s => ({
                    label: statsLabels.source?.[s]?.name ?? s.toUpperCase(),
                    value: s
                  }))}
                  onChange={e => onSourceChange(e.value)}
                />
              ) : (
                <span className="p-inputwrapper-filled">
                  <div className="source-readonly p-inputtext">
                    {statsLabels.source?.[currentSource]?.name ?? currentSource.toUpperCase()}
                  </div>
                </span>
              )}
              <label htmlFor="source-select">{t('filter.source')}</label>
            </span>
          </div>
        )}
      </div>
      {/* Popis metriky */}
      {displayMetric && (
        <div className="metric-description">
            <h1 className="metric-title">{displayMetric.name}</h1>
            {displayMetric.description && (
                <h3 className="metric-descp-text">{displayMetric.description}</h3>
            )}
        </div>
    )}
    </div>
  )
}

export { StatsTopBarSelector }
